import React, { useState } from 'react'
import { useAtom } from 'jotai'
import { useAuth } from '../../contexts/AuthContext'
import { useOrganization } from '../../contexts/OrganizationContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { 
  Building2, 
  Users, 
  ArrowRight, 
  Check, 
  AlertCircle,
  Loader2,
  Globe,
  UserPlus
} from 'lucide-react'
import { 
  organizationSetupAtom,
  setOrganizationSetupStepAtom,
  setOrganizationSetupLoadingAtom,
  setOrganizationSetupErrorAtom,
  currentOrganizationAtom,
  currentMembershipAtom
} from '../../store/organizationAtoms'
import { organizationService, memberService, generateSlug } from '../../services/firestoreService'

const OrganizationSetup: React.FC = () => {
  const { currentUser } = useAuth()
  const { refreshOrganization } = useOrganization()
  const [setup] = useAtom(organizationSetupAtom)
  const [, setSetupStep] = useAtom(setOrganizationSetupStepAtom)
  const [, setSetupLoading] = useAtom(setOrganizationSetupLoadingAtom)
  const [, setSetupError] = useAtom(setOrganizationSetupErrorAtom)
  const [, setCurrentOrganization] = useAtom(currentOrganizationAtom)
  const [, setCurrentMembership] = useAtom(currentMembershipAtom)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    website: ''
  })
  const [slugChecking, setSlugChecking] = useState(false)
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Auto-generate slug from name
    if (field === 'name') {
      const generatedSlug = generateSlug(value)
      setFormData(prev => ({ ...prev, slug: generatedSlug }))
      setSlugAvailable(null)
    }
    
    // Check slug availability
    if (field === 'slug') {
      setSlugAvailable(null)
      if (value.length >= 3) {
        checkSlugAvailability(value)
      }
    }
  }

  const checkSlugAvailability = async (slug: string) => {
    setSlugChecking(true)
    try {
      const available = await organizationService.isSlugAvailable(slug)
      setSlugAvailable(available)
    } catch (error) {
      console.error('Error checking slug:', error)
    } finally {
      setSlugChecking(false)
    }
  }

  const handleCreateOrganization = async () => {
    if (!currentUser) return
    
    setSetupLoading(true)
    setSetupError(null)
    
    try {
      // Validate form
      if (!formData.name.trim() || !formData.slug.trim()) {
        throw new Error('Organization name and slug are required')
      }
      
      if (slugAvailable === false) {
        throw new Error('This slug is already taken')
      }
      
      // Create organization
      const organization = await organizationService.create({
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim() || undefined,
        website: formData.website.trim() || undefined,
        ownerId: currentUser.uid
      })
      
      // Create owner membership
      const membership = await memberService.create({
        organizationId: organization.id,
        userId: currentUser.uid,
        email: currentUser.email || '',
        displayName: currentUser.displayName || undefined,
        role: 'owner',
        status: 'active'
      })
      
      // Set current organization and membership
      setCurrentOrganization(organization)
      setCurrentMembership(membership)
      
      // Refresh organization data
      await refreshOrganization()
      
      // Complete setup
      setSetupStep('complete')
      
    } catch (error) {
      console.error('Error creating organization:', error)
      setSetupError(error instanceof Error ? error.message : 'Failed to create organization')
    } finally {
      setSetupLoading(false)
    }
  }

  const renderStepSelector = () => (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl">Setup Your Organization</CardTitle>
        <CardDescription className="text-base">
          To save charts to the database, you need to be part of an organization. 
          You can create a new one or join an existing organization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-primary/20"
                onClick={() => setSetupStep('create')}>
            <CardContent className="p-6 text-center">
              <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Create Organization</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Start fresh with your own organization and invite team members.
              </p>
              <Badge variant="secondary">Recommended</Badge>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-primary/20"
                onClick={() => setSetupStep('join')}>
            <CardContent className="p-6 text-center">
              <UserPlus className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Join Organization</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Join an existing organization using an invitation link.
              </p>
              <Badge variant="outline">Coming Soon</Badge>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  )

  const renderCreateForm = () => (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Create Your Organization
        </CardTitle>
        <CardDescription>
          Set up your organization to start saving and sharing charts with your team.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {setup.error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <p className="text-sm text-destructive">{setup.error}</p>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Organization Name *</Label>
            <Input
              id="name"
              placeholder="Acme Inc."
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={setup.loading}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="slug">URL Slug *</Label>
            <div className="relative">
              <Input
                id="slug"
                placeholder="acme-inc"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                disabled={setup.loading}
                className={
                  slugAvailable === false ? 'border-destructive' :
                  slugAvailable === true ? 'border-green-500' : ''
                }
              />
              {slugChecking && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin" />
              )}
              {slugAvailable === true && (
                <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
              {slugAvailable === false && (
                <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-destructive" />
              )}
            </div>
            {slugAvailable === false && (
              <p className="text-xs text-destructive">This slug is already taken</p>
            )}
            {slugAvailable === true && (
              <p className="text-xs text-green-600">This slug is available</p>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Brief description of your organization..."
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            disabled={setup.loading}
            rows={3}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="website"
              placeholder="https://acme.com"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              disabled={setup.loading}
              className="pl-10"
            />
          </div>
        </div>
        
        <div className="flex gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={() => setSetupStep('none')}
            disabled={setup.loading}
          >
            Back
          </Button>
          <Button 
            onClick={handleCreateOrganization}
            disabled={setup.loading || !formData.name.trim() || !formData.slug.trim() || slugAvailable === false}
            className="flex-1"
          >
            {setup.loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                Create Organization
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  const renderJoinForm = () => (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Join Organization
        </CardTitle>
        <CardDescription>
          This feature is coming soon. For now, please create your own organization.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8">
          <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-6">
            Organization invitations and joining will be available in a future update.
          </p>
          <Button variant="outline" onClick={() => setSetupStep('none')}>
            Back to Options
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  const renderComplete = () => (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <CardTitle className="text-2xl text-green-600">Organization Created!</CardTitle>
        <CardDescription className="text-base">
          Your organization has been successfully created. You can now save charts to the database.
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center">
        <Button onClick={() => setSetupStep('none')}>
          Continue to Dashboard
        </Button>
      </CardContent>
    </Card>
  )

  switch (setup.step) {
    case 'create':
      return renderCreateForm()
    case 'join':
      return renderJoinForm()
    case 'complete':
      return renderComplete()
    default:
      return renderStepSelector()
  }
}

export default OrganizationSetup 